import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

const LEVEL_META = {
  bronze: { label: '🥉 Bronze', color: '#cd7f32', next: 500 },
  silver: { label: '🥈 Silver', color: '#c0c0c0', next: 2000 },
  gold:   { label: '🥇 Gold',   color: '#ffd700', next: null },
};

const DEFAULT_RATES = { booking_coins: 50, referral_coins: 200, coin_earn_rate: 0 };

export default function Promo() {
  const { t } = useLang();
  const [codes, setCodes]   = useState([]);
  const [tab, setTab]       = useState('codes');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]     = useState({ code: '', discount_type: 'percent', discount_value: '', min_amount: '', max_uses: '', expires_at: '' });
  const [labs, setLabs]     = useState([]);
  const [labId, setLabId]   = useState('');
  const [loading, setLoading] = useState(false);

  const [rates, setRates]       = useState(DEFAULT_RATES);
  const [ratesSaving, setRatesSaving] = useState(false);
  const [ratesMsg, setRatesMsg] = useState('');

  useEffect(() => {
    api.get('/owner/labs').then(r => {
      setLabs(r.data);
      if (r.data.length) setLabId(parseInt(localStorage.getItem('selected_lab_id')) || r.data[0].id);
    }).catch(() => {});
    loadCodes();
  }, []);

  useEffect(() => {
    if (!labId) return;
    api.get(`/promo/loyalty/settings?lab_id=${labId}`)
      .then(r => setRates({ booking_coins: r.data.booking_coins ?? 50, referral_coins: r.data.referral_coins ?? 200, coin_earn_rate: r.data.coin_earn_rate ?? 0, new_user_coins: r.data.new_user_coins ?? 0 }))
      .catch(() => {});
  }, [labId]);

  const saveRates = async () => {
    if (!labId) return;
    setRatesSaving(true); setRatesMsg('');
    try {
      await api.put('/promo/loyalty/settings', { lab_id: labId, ...rates });
      setRatesMsg('Saqlandi ✓');
      setTimeout(() => setRatesMsg(''), 2500);
    } catch (e) {
      setRatesMsg(e.response?.data?.error || 'Xato');
    } finally { setRatesSaving(false); }
  };

  const loadCodes = () => {
    api.get('/promo').then(r => setCodes(r.data)).catch(() => {});
  };

  const createCode = async () => {
    if (!form.code || !form.discount_value) return;
    setLoading(true);
    try {
      await api.post('/promo', {
        ...form,
        lab_id: labId || null,
        discount_value: parseFloat(form.discount_value),
        min_amount: parseFloat(form.min_amount) || 0,
        max_uses: parseInt(form.max_uses) || 0,
        expires_at: form.expires_at || null,
      });
      loadCodes();
      setForm({ code: '', discount_type: 'percent', discount_value: '', min_amount: '', max_uses: '', expires_at: '' });
      setShowForm(false);
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally { setLoading(false); }
  };

  const toggleActive = async (id, current) => {
    try {
      await api.put(`/promo/${id}`, { is_active: !current });
      setCodes(prev => prev.map(c => c.id === id ? { ...c, is_active: !current } : c));
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars[Math.floor(Math.random() * chars.length)];
    setForm(f => ({ ...f, code }));
  };

  const TABS = [['codes', t('tabPromoCode')], ['loyalty', t('tabLoyalty')]];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>{t('promoTitle')}</h1>
        <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>{t('promoSubtitle')}</div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '7px 16px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
            background: tab === k ? 'rgba(0,212,255,0.1)' : 'transparent',
            border: `1px solid ${tab === k ? 'var(--cyan)' : 'var(--border)'}`,
            color: tab === k ? 'var(--cyan)' : 'var(--text2)',
          }}>{l}</button>
        ))}
      </div>

      {tab === 'codes' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button onClick={() => setShowForm(!showForm)} style={{
              background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)',
              color: 'var(--cyan)', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
            }}>{t('promoNewCode')}</button>
          </div>

          {showForm && (
            <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="KOD *"
                    style={{ flex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12, fontFamily: 'Orbitron, sans-serif' }} />
                  <button onClick={generateCode} style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid var(--purple)', color: 'var(--purple)', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 11 }}>🎲</button>
                </div>

                <select value={form.discount_type} onChange={e => setForm(f => ({ ...f, discount_type: e.target.value }))} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }}>
                  <option value="percent">{t('promoDiscountPercent')}</option>
                  <option value="fixed">{t('promoDiscountFixed')}</option>
                </select>

                <input value={form.discount_value} onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                  placeholder={form.discount_type === 'percent' ? t('promoDiscountPctPlh') : t('promoDiscountSumPlh')} type="number"
                  style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />

                <input value={form.min_amount} onChange={e => setForm(f => ({ ...f, min_amount: e.target.value }))} placeholder={t('promoMinAmountPlh')} type="number"
                  style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />

                <input value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} placeholder={t('promoMaxUsesPlh')} type="number"
                  style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />

                <input value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} type="datetime-local"
                  style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />
              </div>

              {false && labs.length > 1 && (
                <select value={labId} onChange={e => setLabId(e.target.value)} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12, width: '100%' }}>
                  <option value="">{t('allRoomsOption')}</option>
                  {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={createCode} disabled={loading} style={{ background: 'var(--cyan)', border: 'none', color: '#000', padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, opacity: loading ? 0.6 : 1 }}>{t('promoCreateBtn')}</button>
                <button onClick={() => setShowForm(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>{t('cancelShort')}</button>
              </div>
            </div>
          )}

          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 100px 120px 100px 100px 120px 80px', padding: '10px 20px', borderBottom: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)', letterSpacing: 1, textTransform: 'uppercase' }}>
              <span>{t('colCode')}</span><span>{t('colDiscount')}</span><span>{t('colMinAmount')}</span><span>{t('colUsage')}</span><span>{t('colExpiry')}</span><span>{t('colState')}</span><span></span>
            </div>

            {codes.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>{t('promoNoCodes')}</div>
            ) : codes.map((c, i) => (
              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '140px 100px 120px 100px 100px 120px 80px', padding: '13px 20px', borderBottom: i < codes.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center', fontSize: 12 }}>
                <span style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--cyan)', fontSize: 11, fontWeight: 700 }}>{c.code}</span>
                <span style={{ color: 'var(--green)', fontWeight: 600 }}>
                  {c.discount_type === 'percent' ? `${c.discount_value}%` : `${Number(c.discount_value).toLocaleString()} ${t('currency')}`}
                </span>
                <span style={{ color: 'var(--text2)' }}>{c.min_amount > 0 ? `${Number(c.min_amount).toLocaleString()}` : '—'}</span>
                <span style={{ color: 'var(--text2)' }}>
                  {c.uses_count}/{c.max_uses === 0 ? '∞' : c.max_uses}
                </span>
                <span style={{ fontSize: 10, color: 'var(--text3)' }}>
                  {c.expires_at ? new Date(c.expires_at).toLocaleDateString('uz-UZ') : '—'}
                </span>
                <span style={{
                  display: 'inline-block',
                  background: c.is_active ? 'rgba(0,255,136,0.12)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${c.is_active ? 'var(--green)' : 'var(--border)'}`,
                  color: c.is_active ? 'var(--green)' : 'var(--text3)',
                  padding: '2px 10px', borderRadius: 20, fontSize: 10,
                }}>{c.is_active ? t('promoStatusActive') : t('promoStatusInactive')}</span>
                <button onClick={() => toggleActive(c.id, c.is_active)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 10 }}>
                  {c.is_active ? t('promoToggleOff') : t('promoToggleOn')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'loyalty' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
            {Object.entries(LEVEL_META).map(([key, meta]) => (
              <div key={key} style={{ background: 'var(--card2)', border: `1px solid ${meta.color}40`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>{meta.label.split(' ')[0]}</div>
                <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 16, fontWeight: 700, color: meta.color, marginBottom: 4 }}>{meta.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                  {key === 'bronze' && '0 – 499 ball'}
                  {key === 'silver' && '500 – 1999 ball'}
                  {key === 'gold' && '2000+ ball'}
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t('loyaltyPointsTitle')}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 16 }}>
              Har bir harakat uchun neча ball berilinishi — siz sozlaysiz
            </div>

            {labs.length > 1 && (
              <select value={labId} onChange={e => setLabId(parseInt(e.target.value))}
                style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 12px', borderRadius: 8, fontSize: 12, marginBottom: 16, width: '100%' }}>
                {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {[
                { key: 'coin_earn_rate', label: '💰 Hisobga pul qo\'shish', unit: '% (topup summasidan ball)', step: 0.5 },
                { key: 'booking_coins',  label: t('loyaltyBooking'),  unit: 'ball / bron', step: 1 },
                { key: 'referral_coins', label: t('loyaltyReferral'), unit: 'ball / do\'st taklif', step: 1 },
                { key: 'new_user_coins', label: '🎉 Yangi ro\'yxatdan o\'tish', unit: 'ball (birinchi kirish)', step: 1 },
              ].map(({ key, label, unit, step }) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, gap: 12 }}>
                  <span style={{ fontSize: 13, flex: 1 }}>{label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="number" min={step} step={step}
                      value={rates[key]}
                      onChange={e => setRates(r => ({ ...r, [key]: step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value) }))}
                      style={{ width: 72, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '5px 8px', borderRadius: 6, fontSize: 13, textAlign: 'right' }}
                    />
                    <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{unit}</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={saveRates} disabled={ratesSaving}
                style={{ background: 'var(--cyan)', border: 'none', color: '#000', padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700, opacity: ratesSaving ? 0.6 : 1 }}>
                {ratesSaving ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
              {ratesMsg && <span style={{ fontSize: 12, color: ratesMsg.includes('✓') ? 'var(--green)' : 'var(--red)' }}>{ratesMsg}</span>}
            </div>

            <div style={{ marginTop: 16, padding: 14, background: 'rgba(124,58,237,0.08)', border: '1px solid var(--purple)', borderRadius: 10, fontSize: 12, color: 'var(--text2)' }}>
              💡 {t('loyaltyNote')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
